import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { withAuth } from "@cdk-prod/fortellis-auth-context";
import WithNavigationToHome from "../components/WithNavigationToHome";
import ChevronIcon from "../components/ChevronIcon";
import {
  Grid,
  Row,
  Column,
  IndeterminateProgressCircle,
  INPUT_SIZES,
  SearchInput,
} from "cdk-radial";

const AvailableRoles = (props) => {
  const { auth, fetchAllRoles } = props;
  const [openRoles, setOpenRoles] = useState({});
  const [openEntitlements, setOpenEntitlements] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  const allRolesList = useMemo(() => props.allRoles || [], [props.allRoles]);

  useEffect(() => {
    const authToken = auth?.accessToken;
    if (authToken) {
      fetchAllRoles({ accessToken: authToken });
    }
  }, [auth]);

  const sortedRoles = useMemo(() => {
    return [...allRolesList].sort((a, b) => a.name.localeCompare(b.name));
  }, [allRolesList]);

  const handleSearchChange = (val) => {
    const trimmed = val.trim();
    setSearchTerm(trimmed);

    if (trimmed.length === 0) {
      setOpenRoles({});
      setOpenEntitlements({});
      return;
    }

    const lowerSearch = trimmed.toLowerCase();
    const newOpenRoles = {};
    const newOpenEntitlements = {};

    sortedRoles.forEach((role) => {
      const roleNameMatch = role.name.toLowerCase().includes(lowerSearch);
      let matchedEntitlement = false;

      (role.permissions || []).forEach((entitlement) => {
        const entitlementMatch = entitlement.name.toLowerCase().includes(lowerSearch);
        const actionMatch = (entitlement.actions || []).some((action) =>
          action.toLowerCase().includes(lowerSearch)
        );

        if (entitlementMatch || actionMatch) {
          matchedEntitlement = true;
          newOpenEntitlements[`${role.id}-${entitlement.name}`] = true;
        }
      });

      if (roleNameMatch || matchedEntitlement) {
        newOpenRoles[role.id] = true;
      }
    });

    setOpenRoles(newOpenRoles);
    setOpenEntitlements(newOpenEntitlements);
  };

  const filteredRoles = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase();
    return sortedRoles.filter((role) => {
      const roleNameMatch = role.name.toLowerCase().includes(lowerSearch);
      const actionMatch = role.permissions?.some((entitlement) =>
        (entitlement.actions || []).some((action) =>
          action.toLowerCase().includes(lowerSearch)
        )
      );
      const entitlementMatch = role.permissions?.some((entitlement) =>
        entitlement.name.toLowerCase().includes(lowerSearch)
      );
      return roleNameMatch || entitlementMatch || actionMatch;
    });
  }, [sortedRoles, searchTerm]);

  const toggleRole = (roleId) => {
    setOpenRoles((prev) => ({
      ...prev,
      [roleId]: !prev[roleId],
    }));
  };

  const toggleEntitlement = (entitlementId) => {
    setOpenEntitlements((prev) => ({
      ...prev,
      [entitlementId]: !prev[entitlementId],
    }));
  };

  if (!props.allRoles || props.allRoles.length === 0) {
    return (
      <div className="loading-container">
        <h4>Loading Roles</h4>
        <IndeterminateProgressCircle />
      </div>
    );
  }

  return (
    <div style={{ padding: "2rem", maxWidth: "1400px", margin: "0 auto" }}>
      <h2 style={{ fontSize: "1.75rem", color: "white" }}>Available Roles</h2>

      <div style={{ maxWidth: "300px", marginBottom: "1rem" }}>
        <SearchInput
          clearButtonLabel="Clear"
          id="search-input-expanded"
          isAlwaysExpanded
          name="search-input-expanded"
          onChange={handleSearchChange}
          placeholder="Search for role, entitlement, or action..."
          size={INPUT_SIZES.STANDARD}
          value={searchTerm}
        />
      </div>

      <div style={{ marginTop: "2rem", minHeight: "2000px" }}>
        <Grid>
          <Row>
            {filteredRoles.length > 0 ? (
              filteredRoles.map((role) => (
                <Column key={role.id} xl={3} l={4} m={6} s={12} xs={12}>
                  <div
                    style={{
                      width: "100%",
                      minHeight: "70px",
                      height: "auto",
                      overflow: "visible",
                      border: "1px solid #ccc",
                      borderRadius: "8px",
                      padding: "1rem",
                      margin: "0.5rem 0",
                      backgroundColor: "#f9f9f9",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      boxSizing: "border-box",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-start",
                    }}
                  >
                    <div
                      onClick={() => toggleRole(role.id)}
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <h4
                        style={{
                          margin: 0,
                          fontSize: "1.1rem",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {role.name}
                      </h4>
                      <ChevronIcon isOpen={openRoles[role.id]} />
                    </div>

                    {openRoles[role.id] && (
                      <div style={{ marginTop: "1rem" }}>
                        <strong>Entitlements:</strong>
                        <div style={{ marginTop: "0.5rem" }}>
                          {(role.permissions || []).map((entitlement) => {
                            const entitlementId = `${role.id}-${entitlement.name}`;
                            return (
                              <div key={entitlementId} style={{ marginBottom: "0.5rem" }}>
                                <div
                                  onClick={() => toggleEntitlement(entitlementId)}
                                  style={{
                                    cursor: "pointer",
                                    backgroundColor: "#e0e0e0",
                                    padding: "0.5rem",
                                    borderRadius: "4px",
                                    fontWeight: "bold",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  {entitlement.name}
                                  <ChevronIcon isOpen={openEntitlements[entitlementId]} />
                                </div>

                                {openEntitlements[entitlementId] && (
                                  <ul
                                    style={{
                                      paddingLeft: "1.5rem",
                                      marginTop: "0.5rem",
                                    }}
                                  >
                                    {(entitlement.actions || []).map((action, i) => (
                                      <li key={i} style={{ fontSize: "0.85rem" }}>
                                        {action}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </Column>
              ))
            ) : (
              <Column s={12}>
                <div style={{ padding: "1rem", color: "#666" }}>
                  No roles, entitlements, or actions match your search.
                </div>
              </Column>
            )}
          </Row>
        </Grid>
      </div>
    </div>
  );
};

const ConnectedAvailableRoles = WithNavigationToHome(connect()(AvailableRoles));

const AvailableRolesContainer = (props) => {
  return props.auth?.isAuthenticated ? (
    <ConnectedAvailableRoles {...props} />
  ) : (
    <div />
  );
};

export default withAuth(AvailableRolesContainer);
